import { FileSheetLabel } from "@/components/ui/file-sheet-label";
import { GoToRowLink } from "@/components/verification/GoToRowLink";
import { fmtNum, fmtRub, fmtRubShort } from "@/lib/format";
import { formatMessage, reconciliationResultMessages } from "@/services/messages";
import type { ActSourceRef, WorkOrderSourceRef } from "@efes/shared";
import { useCallback, useMemo, useState } from "react";
import { compareSourceFile } from "./lib/source-sort";

/**
 * Таблица источников внутри карточки расхождения (фича 024 ① — aggregated card v2).
 *
 * Универсальный примитив: рендерит либо акт-, либо WO-источники одной таблицей
 * с сортировкой, sparkbar в колонке стоимости, hover-actions и Σ-итого.
 *
 * Локальный state — только sortBy/sortDir. Открытие/закрытие таблицы и фильтрация
 * остаются за родителем (см. handoff README, Screen 1, секция «Sources table»).
 *
 * Default sort: `cost desc`. Цикл по клику: `desc → asc → desc` (не сбрасывается,
 * как в дизайн-эталоне `OptionOneV2`, строки 99–215).
 */

type SortField = "file" | "row" | "qty" | "cost";
type SortDir = "asc" | "desc";
type AnySource = ActSourceRef | WorkOrderSourceRef;

type DiscrepancyAggregatedTableCommonProps = {
	/** Единица измерения объёма для заголовка колонки и подсказки. */
	unit: string | null;
	/** Опциональный обработчик клика на ссылку строки (excel deep-link). */
	onSourceClick?: (source: ActSourceRef | WorkOrderSourceRef) => void;
};

export type DiscrepancyAggregatedTableProps = DiscrepancyAggregatedTableCommonProps &
	(
		| { kind: "act"; sources: ActSourceRef[] }
		| { kind: "work-order"; sources: WorkOrderSourceRef[] }
	);

export function DiscrepancyAggregatedTable({
	sources,
	kind,
	unit,
	onSourceClick,
}: DiscrepancyAggregatedTableProps) {
	const messages = reconciliationResultMessages.redesign;
	const [sortBy, setSortBy] = useState<SortField>("cost");
	const [sortDir, setSortDir] = useState<SortDir>("desc");

	const getCost = useCallback(
		(s: AnySource): number =>
			kind === "act" ? (s as ActSourceRef).salaryTotal : (s as WorkOrderSourceRef).amount,
		[kind],
	);
	const getRow = useCallback(
		(s: AnySource): number => {
			if (s.excelRow != null) return s.excelRow;
			// WorkOrderSourceRef имеет обязательное rowNumber; ActSourceRef — нет.
			if (kind === "work-order") return (s as WorkOrderSourceRef).rowNumber;
			const act = s as ActSourceRef;
			return act.lineId ?? act.blockNumber ?? 0;
		},
		[kind],
	);

	const sorted = useMemo(() => {
		if (sources.length === 0) return [] as AnySource[];
		const list = [...(sources as AnySource[])];
		const m = sortDir === "desc" ? -1 : 1;
		const tiebreak = (a: AnySource, b: AnySource): number => {
			const byFile = compareSourceFile(a, b);
			if (byFile !== 0) return byFile;
			return getRow(a) - getRow(b);
		};
		list.sort((a, b) => {
			let primary: number;
			if (sortBy === "qty") primary = (a.quantity - b.quantity) * m;
			else if (sortBy === "cost") primary = (getCost(a) - getCost(b)) * m;
			else if (sortBy === "row") primary = (getRow(a) - getRow(b)) * m;
			else primary = compareSourceFile(a, b) * m;
			if (primary !== 0) return primary;
			return tiebreak(a, b);
		});
		return list;
	}, [sources, sortBy, sortDir, getCost, getRow]);

	if (sources.length === 0) return null;

	const totalQty = (sources as AnySource[]).reduce((acc, s) => acc + s.quantity, 0);
	// Σ — сумма Math.round, чтобы visually совпадала с пользовательским
	// сложением колонки (33,6 показывается как «34» через fmtRub).
	const totalCost = (sources as AnySource[]).reduce((acc, s) => acc + Math.round(getCost(s)), 0);
	const maxCost = (sources as AnySource[]).reduce((acc, s) => Math.max(acc, getCost(s)), 0);

	const onHeaderClick = (id: SortField) => {
		if (sortBy === id) {
			setSortDir((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setSortBy(id);
			setSortDir("desc");
		}
	};

	const volumeColLabel = formatMessage(messages.aggregated_table_col_volume_template, {
		unit: unit ?? "—",
	});

	return (
		<table className="w-full table-fixed font-mono text-xs">
			<thead className="border-slate-200 border-y bg-white">
				<tr>
					<HeaderCell
						active={sortBy === "file"}
						dir={sortDir}
						onClick={() => onHeaderClick("file")}
						widthClass="w-[60%]"
					>
						{messages.aggregated_table_col_file}
					</HeaderCell>
					<HeaderCell
						active={sortBy === "row"}
						dir={sortDir}
						onClick={() => onHeaderClick("row")}
						widthClass="w-[64px]"
						numeric
					>
						{messages.aggregated_table_col_row}
					</HeaderCell>
					<HeaderCell
						active={sortBy === "qty"}
						dir={sortDir}
						onClick={() => onHeaderClick("qty")}
						widthClass="w-[88px]"
						numeric
					>
						{volumeColLabel}
					</HeaderCell>
					<HeaderCell
						active={sortBy === "cost"}
						dir={sortDir}
						onClick={() => onHeaderClick("cost")}
						widthClass="w-[160px]"
						numeric
					>
						{messages.aggregated_table_col_cost}
					</HeaderCell>
				</tr>
			</thead>
			<tbody className="divide-y divide-slate-100">
				{sorted.map((source) => (
					<SourceRow
						key={`${source.fileId}-${getRow(source)}-${"blockId" in source ? source.blockId : "wo"}-${"lineId" in source ? (source.lineId ?? "") : ""}`}
						source={source}
						kind={kind}
						maxCost={maxCost}
						getCost={getCost}
						getRow={getRow}
						onSourceClick={onSourceClick}
						blockTemplate={messages.aggregated_table_block_template}
					/>
				))}
				<tr className="bg-slate-100">
					<td className="px-2 py-1.5 font-sans font-semibold text-[10px] text-slate-500 uppercase tracking-wide">
						{messages.aggregated_table_total_label}
					</td>
					<td />
					<td className="px-2 py-1.5 text-right font-semibold tabular-nums text-slate-900">
						{fmtNum(totalQty)}
					</td>
					<td className="px-2 py-1.5 text-right font-semibold tabular-nums text-red-800">
						{fmtRub(totalCost)}
					</td>
				</tr>
			</tbody>
		</table>
	);
}

interface HeaderCellProps {
	children: React.ReactNode;
	active: boolean;
	dir: SortDir;
	onClick: () => void;
	numeric?: boolean;
	widthClass?: string;
}

function HeaderCell({ children, active, dir, onClick, numeric, widthClass }: HeaderCellProps) {
	return (
		<th
			className={`whitespace-nowrap px-2 py-1.5 font-medium text-[10.5px] text-slate-500 uppercase tracking-wide ${numeric ? "text-right" : "text-left"} ${widthClass ?? ""}`}
			aria-sort={active ? (dir === "desc" ? "descending" : "ascending") : "none"}
		>
			<button
				type="button"
				onClick={onClick}
				className="inline-flex items-center gap-1 hover:text-slate-900"
			>
				{children}
				{active ? <span className="text-[8px]">{dir === "desc" ? "▼" : "▲"}</span> : null}
			</button>
		</th>
	);
}

interface SourceRowProps {
	source: AnySource;
	kind: "act" | "work-order";
	maxCost: number;
	getCost: (s: AnySource) => number;
	getRow: (s: AnySource) => number;
	onSourceClick?: (source: AnySource) => void;
	blockTemplate: string;
}

function SourceRow({
	source,
	kind,
	maxCost,
	getCost,
	getRow,
	onSourceClick,
	blockTemplate,
}: SourceRowProps) {
	const cost = getCost(source);
	const pct = maxCost > 0 ? (cost / maxCost) * 100 : 0;
	const rowNum = getRow(source);

	return (
		<tr className="group hover:bg-white">
			<td className="px-2 py-1.5 align-top">
				<div className="min-w-0">
					<div
						className="font-sans font-medium text-[12px] text-slate-900 leading-snug line-clamp-2 break-words"
						title={source.workName || undefined}
					>
						{source.workName || "—"}
					</div>
					<div className="mt-0.5 flex min-w-0 items-center gap-1.5">
						<span className="min-w-0 flex-1 text-[10.5px] text-slate-500">
							<FileSheetLabel fileName={source.fileName} sheetName={source.sheetName} />
						</span>
						{kind === "act" ? (
							<span className="inline-flex shrink-0 rounded bg-slate-100 px-1 text-[10px] text-slate-500">
								{formatMessage(blockTemplate, {
									block: (source as ActSourceRef).blockNumber,
								})}
							</span>
						) : null}
					</div>
				</div>
			</td>
			<td className="px-2 py-1.5 text-right">
				<GoToRowLink
					excelRow={rowNum}
					continuationRows={"continuationRows" in source ? source.continuationRows : undefined}
					label={String(rowNum)}
					onClick={onSourceClick ? () => onSourceClick(source) : undefined}
					fileId={source.fileId}
					sheet={source.sheetName ?? null}
					className="inline-flex items-center gap-0.5 text-blue-600 tabular-nums hover:underline focus:outline-none focus:ring-1 focus:ring-blue-400 rounded"
				/>
			</td>
			<td className="px-2 py-1.5 text-right tabular-nums text-slate-900">
				{fmtNum(source.quantity)}
			</td>
			<td className="px-2 py-1.5 text-right">
				<div className="flex items-center justify-end gap-2">
					<div className="h-1 w-16 overflow-hidden rounded-sm bg-slate-100">
						<span className="block h-full bg-red-300" style={{ width: `${pct}%` }} />
					</div>
					<span className="tabular-nums text-slate-900">{fmtRubShort(cost)}</span>
				</div>
			</td>
		</tr>
	);
}
